<template>
	<!-- Floating launcher, hidden while the drawer is open. Teleported to body so Fomantic's .pusher
		transform can't trap position:fixed on tall pages. -->
	<teleport to="body">
		<button v-if="!open" class="assist-fab" @click="open = true" title="Asistente">
			<i class="comment alternate outline icon"></i>
		</button>
	</teleport>

	<el-drawer
		v-model="open"
		title="Asistente"
		direction="rtl"
		:size="drawerSize"
		:append-to-body="true"
		:modal-append-to-body="true"
		class="assist-drawer"
	>
		<div class="assist-body">
			<!-- Explicit routing: Datos = SQL over the DB, Sistema = help docs. Beats guessing. -->
			<div class="assist-modes">
				<button :class="['assist-mode', { active: mode === 'data' }]" @click="setMode('data')">Datos</button>
				<button :class="['assist-mode', { active: mode === 'help' }]" @click="setMode('help')">Sistema</button>
			</div>
			<div class="assist-messages" ref="scroll">
				<div v-if="messages.length === 0" class="assist-hint">
					{{ mode === 'data'
						? 'Pregunta sobre tus datos: ventas, pedidos, clientes, inventario…'
						: 'Pregunta cómo usar el sistema o esta pantalla.' }}
				</div>
				<div v-for="(m, i) in messages" :key="i" :class="['assist-msg', m.role, { 'has-table': m.rows && m.rows.length }]">
					<div class="assist-text">
						<span>{{ m.content }}</span>
						<div v-if="m.rows && m.rows.length" class="assist-table-wrap">
							<table class="assist-table">
								<thead>
									<tr><th v-for="c in cols(m.rows)" :key="c">{{ c }}</th></tr>
								</thead>
								<tbody>
									<tr v-for="(r, ri) in m.rows.slice(0, 50)" :key="ri">
										<td v-for="c in cols(m.rows)" :key="c">{{ fmt(r[c]) }}</td>
									</tr>
								</tbody>
							</table>
							<div v-if="m.rows.length > 50" class="assist-more">…y {{ m.rows.length - 50 }} filas más</div>
						</div>
					</div>
				</div>
				<div v-if="loading" class="assist-msg assistant"><span class="assist-text assist-dots">…</span></div>
			</div>
			<div class="assist-input">
				<el-input
					v-model="question"
					:placeholder="mode === 'data' ? 'Pregunta sobre tus datos' : 'Pregunta sobre el sistema'"
					:disabled="loading"
					@keyup.enter="ask"
				/>
				<el-button type="primary" :loading="loading" @click="ask">Enviar</el-button>
			</div>
		</div>
	</el-drawer>
</template>

<script>
import { ref, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import Api from "@/api/Api";

export default {
	name: "AssistantWidget",
	setup() {
		const route = useRoute();
		const toast = useToast();
		const open = ref(false);
		// Fixed panel on desktop, full-width on phones.
		const drawerSize = ref(window.innerWidth < 480 ? "100%" : "380px");
		const question = ref("");
		const loading = ref(false);
		const messages = ref([]); // { role: 'user'|'assistant', content }
		const scroll = ref(null);
		// 'data' = SQL over the DB, 'help' = system docs. Remembered between opens.
		const mode = ref(localStorage.getItem("assistMode") === "help" ? "help" : "data");
		const setMode = (m) => {
			mode.value = m;
			localStorage.setItem("assistMode", m);
		};

		const scrollDown = () => nextTick(() => {
			if (scroll.value) scroll.value.scrollTop = scroll.value.scrollHeight;
		});

		const ask = async () => {
			const q = question.value.trim();
			if (!q || loading.value) return;
			messages.value.push({ role: "user", content: q });
			question.value = "";
			loading.value = true;
			scrollDown();
			try {
				// Send only the last few turns as context (text only — no table rows).
				const history = messages.value.slice(-6).map((m) => ({ role: m.role, content: m.content }));
				const res = await Api.callAssist(q, route.name || route.path, history, mode.value);
				if (res && !res.error) {
					const msg = { role: "assistant", content: res.data.finalAnswer };
					// Show the raw rows as a table when the data path returned any.
					if (Array.isArray(res.data.results) && res.data.results.length) msg.rows = res.data.results;
					messages.value.push(msg);
				} else {
					toast.error((res && res.message_client) || "No se pudo responder.");
				}
			} catch (e) {
				toast.error("Error al contactar al asistente.");
			} finally {
				loading.value = false;
				scrollDown();
			}
		};

		// Table helpers: column names from the first row; readable cell values.
		const cols = (rows) => Object.keys(rows[0] || {});
		const fmt = (v) => (v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v));

		return { open, drawerSize, question, loading, messages, scroll, mode, setMode, ask, cols, fmt };
	},
};
</script>

<!-- Non-scoped: the FAB is teleported to body, outside this component's scoped-style reach. -->
<style>
.assist-fab {
	position: fixed;
	right: 20px;
	bottom: 20px;
	width: 52px;
	height: 52px;
	border-radius: 50%;
	border: none;
	background: #2185d0;
	color: #fff;
	font-size: 20px;
	cursor: pointer;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
	z-index: 3000;
	display: flex;
	align-items: center;
	justify-content: center;
}
.assist-fab:hover { background: #1678c2; }
</style>

<style scoped>
/* Element Plus gives .el-drawer__body flex-grow + padding; turn it into a full-height, no-padding
   flex column so the messages scroll and the input stays pinned at the bottom. */
.assist-drawer :deep(.el-drawer__body) {
	padding: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.assist-body {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}
.assist-modes {
	flex: 0 0 auto;
	display: flex;
	gap: 6px;
	padding: 0 12px 8px;
}
.assist-mode {
	flex: 1;
	padding: 6px 10px;
	border: 1px solid #d6d8dc;
	background: #fff;
	color: #555;
	border-radius: 6px;
	font-size: 13px;
	cursor: pointer;
}
.assist-mode.active {
	background: #2185d0;
	border-color: #2185d0;
	color: #fff;
	font-weight: 600;
}
.assist-messages {
	flex: 1 1 auto;
	overflow-y: auto;
	min-height: 0;
	padding: 12px;
}
.assist-hint { color: #888; font-size: 13px; padding: 8px; }
.assist-msg { margin: 6px 0; display: flex; }
.assist-msg.user { justify-content: flex-end; }
.assist-text {
	display: inline-block;
	max-width: 85%;
	padding: 8px 10px;
	border-radius: 10px;
	white-space: pre-wrap;
	word-break: break-word;
	font-size: 14px;
	line-height: 1.4;
}
.assist-msg.user .assist-text { background: #2185d0; color: #fff; }
.assist-msg.assistant .assist-text { background: #f1f2f4; color: #222; }
.assist-msg.has-table .assist-text { max-width: 100%; width: 100%; }
.assist-dots { letter-spacing: 2px; }

.assist-table-wrap {
	margin-top: 8px;
	max-height: 260px;
	overflow: auto;
	border: 1px solid #e0e2e6;
	border-radius: 6px;
	background: #fff;
}
.assist-table {
	border-collapse: collapse;
	width: 100%;
	font-size: 12px;
}
.assist-table th,
.assist-table td {
	border-bottom: 1px solid #eceef1;
	padding: 5px 8px;
	text-align: left;
	white-space: nowrap;
}
.assist-table th {
	position: sticky;
	top: 0;
	background: #f6f7f9;
	font-weight: 600;
}
.assist-more { font-size: 11px; color: #888; padding: 4px 8px; }
.assist-input {
	flex: 0 0 auto;
	display: flex;
	gap: 6px;
	padding: 10px 12px;
	border-top: 1px solid #ececec;
	background: #fff;
}
</style>
